import { Box, Divider } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { defaultInitialState } from 'store/lamenessFilterSlice'
import { useDebounce } from 'hook/useDebounce'
import useCowsNextPage from 'hook/useNextPage'
import { useVisibleCowsCount } from 'hook/useVisibleCowsCount'
import { IFiltersLamenessTable } from 'models'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import CattlesApiService from 'services/cattlesApiServices'
import { fetchLightGroupList } from 'store/groupsSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { hideDigitizationUi } from 'utils/companyDigitizationFeatures'
import { showMilkYieldData } from 'utils/companyMilkFeatures'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import TableSkeleton from 'ui/skeleton/TableSkeleton'

import Input from 'components/shared/textFields/Input'

import { useTabsController } from 'hooks/useTabsController'
import TabsFilter from 'ui/breadCrumbs/TabsFilter'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import CattlesFilterContent, {
	CattlesFilterStats,
} from '../shared/CattlesFilterContent'
import { filterCowsWithFarmCowNumber } from '../shared/cattleListFilters'
import CattlesTable from '../shared/CattlesTable'
import { CattlesSortOrderings } from '../types/cattlesSortTypes'

interface CattlesListProps {
	activeFilter: number | string
	isGroupPage?: boolean
}

const CattlesList: React.FC<CattlesListProps> = React.memo(props => {
	const { activeFilter, isGroupPage = false } = props
	const dispatch = useAppDispatch()
	const { permissions } = useAppSelector(state => state.authReducer)
	const { userData } = useAppSelector(state => state.userReducer)
	const { lightGroupList } = useAppSelector(state => state.groupsReducer)
	const [page, setPage] = useState<number>(1)
	const [debouncedValue, handleChange, value] = useDebounce('', 800)
	const prevActiveFilterRef = useRef(activeFilter)
	useEffect(() => {
		if (prevActiveFilterRef.current !== activeFilter) {
			prevActiveFilterRef.current = activeFilter
		}
	}, [activeFilter])
	const [params, setParams] =
		useState<IFiltersLamenessTable>(defaultInitialState)
	const [sortOrder, setSortOrder] = useState<CattlesSortOrderings | null>(null)

	const handleSortOrder = useCallback((order: CattlesSortOrderings) => {
		setSortOrder(prev => (prev === order ? null : order))
		setPage(1)
	}, [])

	enum CATTLE_TABS {
		ALL = 'all',
		DIGITIZED = 'digitized',
		UNDIGITIZED = 'undigitized',
	}

	const { tab, changeTab } = useTabsController<CATTLE_TABS>({
		keyName: 'cattles_tab',
		defaultValue: CATTLE_TABS.ALL,
	})
	const prevTabRef = useRef(tab)
	const hideDigitization = hideDigitizationUi(userData?.company?.id)
	const identified = hideDigitization
		? undefined
		: tab === CATTLE_TABS.DIGITIZED
			? true
			: tab === CATTLE_TABS.UNDIGITIZED
				? false
				: undefined
	const activeGroupId =
		activeFilter === 'all' || !activeFilter ? undefined : Number(activeFilter)

	const initialPageSize = 36
	const prop = {
		page,
		activeFilter,
		initialPageSize,
		isFetchCowsListEnabled: true,
		isFetchComplete: true,
		params: params,
		tag_id_search: debouncedValue,
		identified,
		ordering: sortOrder,
	}
	const { cowsList, totalCountPages, fetchingCowsList, availableTags } =
		useCowsNextPage(prop)
	const visibleCowsList = useMemo(
		() => filterCowsWithFarmCowNumber(cowsList),
		[cowsList],
	)
	const { data: visibleTotalCount } = useVisibleCowsCount({
		activeFilter,
		params,
		tag_id_search: debouncedValue,
		identified,
		ordering: sortOrder,
	})
	const paginationTotal =
		visibleTotalCount != null ? visibleTotalCount : totalCountPages
	const { data: undigitizedVisibleCount } = useVisibleCowsCount({
		activeFilter,
		params,
		tag_id_search: debouncedValue,
		identified: false,
		ordering: sortOrder,
		enabled: !hideDigitization,
	})

	useEffect(() => {
		if (prevTabRef.current !== tab) {
			prevTabRef.current = tab
			setPage(1)
		}
	}, [tab])

	useEffect(() => {
		const maxPage = Math.max(1, Math.ceil(paginationTotal / initialPageSize))
		if (page > maxPage) {
			setPage(maxPage)
		}
	}, [initialPageSize, page, paginationTotal])

	const undigitizedCount = undigitizedVisibleCount ?? 0

	const isFilterActive = useMemo(
		() => JSON.stringify(params) !== JSON.stringify(defaultInitialState),
		[params],
	)

	const { data: filterSourceCows = [] } = useQuery({
		queryKey: [
			'cattlesFilterSource',
			activeGroupId,
			debouncedValue,
			identified,
		],
		queryFn: async () => {
			const response = await CattlesApiService.getCowsList(
				activeGroupId,
				undefined,
				1,
				10000,
				defaultInitialState,
				debouncedValue,
				identified,
			)

			return filterCowsWithFarmCowNumber(response.data.results)
		},
		staleTime: 50000,
	})

	const filterStats = useMemo<CattlesFilterStats>(() => {
		const getMinMax = (
			values: number[],
		): { start: number | ''; end: number | '' } => {
			const filteredValues = values.filter(value => Number.isFinite(value))
			if (filteredValues.length === 0) return { start: '', end: '' }

			return {
				start: Math.min(...filteredValues),
				end: Math.max(...filteredValues),
			}
		}

		const now = moment()
		const ageValues = filterSourceCows
			.filter(cow => cow.birth_date)
			.map(cow =>
				Number(now.diff(moment(cow.birth_date), 'years', true).toFixed(1)),
			)
		const lactationValues = filterSourceCows
			.filter(cow => Number(cow.lactation_length) > 0)
			.map(cow => Number(cow.lactation_length))
		const lastMilkingValues = filterSourceCows
			.filter(cow => cow.last_milking_dt)
			.map(cow => Number(cow.last_milking_dt))
		const fatnessValues = filterSourceCows
			.filter(cow => cow.fatness != null)
			.map(cow => Number(cow.fatness))
		const limpnessDaysValues = filterSourceCows
			.filter(cow => cow.limpness_start_dt)
			.map(cow => Math.max(0, now.diff(moment(cow.limpness_start_dt), 'days')))

		return {
			age: getMinMax(ageValues),
			lactation: getMinMax(lactationValues),
			lastMilking: getMinMax(lastMilkingValues),
			fatness: getMinMax(fatnessValues),
			limpnessDays: getMinMax(limpnessDaysValues),
		}
	}, [filterSourceCows])

	const availableGroups = useMemo(() => {
		if (lightGroupList?.length) {
			return lightGroupList
				.map(group => ({ id: group.id, title: group.title }))
				.sort((left, right) => left.title.localeCompare(right.title, 'ru'))
		}

		const groupsMap = new Map<number, string>()
		filterSourceCows.forEach(cow => {
			const groupId = cow.group?.id
			const groupTitle = cow.group?.title
			if (groupId != null && groupTitle) {
				groupsMap.set(groupId, groupTitle)
			}
		})
		return Array.from(groupsMap, ([id, title]) => ({ id, title }))
	}, [filterSourceCows, lightGroupList])

	useEffect(() => {
		if (!lightGroupList) {
			dispatch(fetchLightGroupList())
		}
	}, [dispatch, lightGroupList])

	const returnInitialProps = useCallback(() => {
		setParams(defaultInitialState)
		setPage(1)
	}, [])

	const tabOptions = [
		{ key: CATTLE_TABS.ALL, title: 'Все' },
		{ key: CATTLE_TABS.DIGITIZED, title: 'Оцифрованы' },
		{
			key: CATTLE_TABS.UNDIGITIZED,
			title: 'Не оцифрованы',
			...(undigitizedCount > 0
				? {
						badge: undigitizedCount,
						badgeColor: COLORS.error,
					}
				: {}),
		},
	]

	const showInlineFilter = hideDigitization && !isGroupPage
	const listFilter = !isGroupPage && (
		<CattlesFilterContent
			activeFilters={params}
			setActiveFilters={filterValue => {
				setParams(filterValue)
				setPage(1)
			}}
			isFilterActive={isFilterActive}
			returnInitialProps={returnInitialProps}
			availableStats={filterStats}
			groupId={activeGroupId}
			tagIdSearch={debouncedValue}
			identified={identified}
			showLastMilking={showMilkYieldData(
				userData?.company?.id,
				userData?.company?.dairy_integration,
			)}
			showFatness={
				Boolean(permissions?.license_fatness) && !userData.isFatnessExpired
			}
			showLameness={
				Boolean(permissions?.license_limpness) && !userData.isLamenessExpired
			}
			availableGroups={availableGroups}
			resetInline={showInlineFilter}
		/>
	)

	return (
		<CardStandart
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				padding: '24px',
			}}
		>
			<Box
				display='flex'
				justifyContent='space-between'
				alignItems='center'
				mb={showInlineFilter ? '16px' : 0}
			>
				<TypographyHeader>Список голов</TypographyHeader>
				<Box display='flex' alignItems='center' gap='12px'>
					<Input
						width={'400px'}
						value={value}
						onChange={handleChange}
						label={'Поиск по номеру бирки и ошейника'}
						size='small'
					/>
					{showInlineFilter &&
						(fetchingCowsList && cowsList.length === 0 ? (
							<Skeleton height={32} width={110} borderRadius={8} />
						) : (
							listFilter
						))}
				</Box>
			</Box>

			{!hideDigitization && (
				<Box
					display='flex'
					flexDirection='row'
					alignItems='flex-end'
					gap='8px'
					mb='16px'
				>
					{fetchingCowsList && cowsList.length === 0 ? (
						<>
							<Skeleton height={34} width={320} borderRadius={8} />
							{!isGroupPage && (
								<Skeleton height={40} width={110} borderRadius={8} />
							)}
						</>
					) : (
						<>
							<TabsFilter
								availableTabs={tabOptions}
								selectedKey={tab}
								onClick={key => changeTab(key as CATTLE_TABS)}
							/>
							{listFilter}
						</>
					)}
				</Box>
			)}

			{fetchingCowsList ? (
				<TableSkeleton
					widthForBody='100%'
					heightForBody='34px'
					rowCount={12}
					totalHeight='calc(100vh - 350px)'
				/>
			) : (
				<>
					<CattlesTable
						cattlesInfo={visibleCowsList}
						fetchingCowsList={false}
						params={params}
						setParams={setParams}
						availableTags={availableTags}
						onChangeInput={handleChange}
						inputValue={value}
						sortOrder={sortOrder}
						onSortOrder={handleSortOrder}
						isGroupPage={isGroupPage}
					/>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<Divider />
						<Box>
							{visibleCowsList.length !== 0 && (
								<CustomPagination
									page={page}
									onChangeFromServer={setPage}
									count={initialPageSize}
									totalCount={paginationTotal}
									isResetPage={prevActiveFilterRef.current !== activeFilter}
								/>
							)}
						</Box>
					</Box>
				</>
			)}
		</CardStandart>
	)
})

export default CattlesList

CattlesList.displayName = 'CattlesList'
